import { Controller } from "@hotwired/stimulus";
import {
  createMap,
  fitBounds,
  removeMap,
  addLayerWhenMapVisible,
  addReadOnlyGeomanControls,
  injectLayerControlStyle,
  createToggleButton,
  createTogglePanelContainer,
  MAP_TOGGLE_ICONS,
  hasTooltipValue,
  formatTooltipValue,
  openRouteTreeInNewTab,
} from "../../helpers/map_helpers.js";
import { escapeHtml } from "../../utils.js";

const EARLIER_COLOR = "#0d9488";
const LATER_COLOR = "#c026d3";
const COVERAGE_EARLIER_COLOR = "#0d9488";
const COVERAGE_LATER_COLOR = "#ea580c";
const DIFF_BETTER_COLOR = "#16a34a";
const DIFF_WORSE_COLOR = "#dc2626";

const EARLIER_GRADIENT = {
  0.2: "#155e75",
  0.4: "#0e7490",
  0.6: "#0891b2",
  0.8: "#14b8a6",
  1.0: "#2dd4bf",
};
const LATER_GRADIENT = {
  0.2: "#ea580c",
  0.4: "#e11d48",
  0.6: "#db2777",
  0.8: "#c026d3",
  1.0: "#86198f",
};
// Traffic-light Diff: green = better, red = worse.
const DIFF_BETTER_GRADIENT = {
  0.2: "#4ade80",
  0.55: "#16a34a",
  1.0: "#166534",
};
const DIFF_WORSE_GRADIENT = {
  0.2: "#f87171",
  0.55: "#dc2626",
  1.0: "#991b1b",
};

const HEAT_RADIUS = 18;
const HEAT_BLUR = 12;
const ABSOLUTE_HEAT_MIN_OPACITY = 0.6;
const BOTH_HEAT_MIN_OPACITY = 0.5;
const DIFF_HEAT_MIN_OPACITY = 0.65;

// ~25m at the equator; matches nearby samples across routes with GPS jitter.
const DIFF_MATCH_RADIUS_DEGREES = 0.00025;
const DEFAULT_HEATMAP_MODE = "both";

// Connects to data-controller="sanitation-routes--show-map"
export default class extends Controller {
  static targets = ["container"];
  static values = {
    tooltipMaxLabel: { type: String, default: "max" },
    tooltipAvgLabel: { type: String, default: "avg" },
  };

  connect() {
    const polygonCoordinates = JSON.parse(this.element.dataset.polygon);
    const routeTrees = JSON.parse(this.element.dataset.routeTrees);
    const openEndedRouteCoordinates = JSON.parse(
      this.element.dataset.openEndedRouteCoordinates
    );
    const usingOutOfBoundsRouteTreeFallback =
      this.element.dataset.usingOutOfBoundsRouteTreeFallback === "true";
    const fieldFocus = this.element.dataset.fieldFocus === "true";
    const mapViz = this.element.dataset.mapViz || "";
    const earlierIsComparison =
      this.element.dataset.earlierIsComparison === "true";

    const heatmapFieldValues = JSON.parse(
      this.element.dataset.heatmapFieldValues
    );
    const markerFieldValues = JSON.parse(
      this.element.dataset.markerFieldValues
    );
    const descriptiveFieldValues = JSON.parse(
      this.element.dataset.descriptiveFieldValues
    );
    const comparisonRouteLabel = this.element.dataset.comparisonRouteLabel;
    const comparisonToggleTitle = this.element.dataset.comparisonToggleTitle;
    const comparisonRouteTrees = JSON.parse(
      this.element.dataset.comparisonRouteTrees || "[]"
    );
    const comparisonRouteCoordinates = JSON.parse(
      this.element.dataset.comparisonOpenEndedRouteCoordinates || "[]"
    );
    const comparisonHeatmapFieldValues = JSON.parse(
      this.element.dataset.comparisonHeatmapFieldValues || "{}"
    );
    const comparisonMarkerFieldValues = JSON.parse(
      this.element.dataset.comparisonMarkerFieldValues || "{}"
    );

    this.map = createMap(this.containerTarget);
    this.fieldFocus = fieldFocus;
    this.mapViz = mapViz;
    this.heatmapMode = DEFAULT_HEATMAP_MODE;
    this.heatLayersByMode = {
      earlier: null,
      later: null,
      both: null,
      diff: null,
    };
    this.activeHeatmapMode = null;
    this.coverageVisible = false;
    this.coverageLayer = null;
    this.diffResult = null;
    this.coverageToggleButton = null;
    this.coverageToggleContainer = null;

    const polygonLayer = L.geoJSON(polygonCoordinates).addTo(this.map);

    let polyLineLayer;
    let routeTreesLayer;
    let comparisonPolyLineLayer;
    let comparisonRouteTreesLayer;

    if (!fieldFocus) {
      const routeLineCoordinates =
        openEndedRouteCoordinates.length > 0
          ? openEndedRouteCoordinates
          : routeTrees.map((tree) => tree.coordinates);

      if (routeLineCoordinates.length > 0) {
        polyLineLayer = L.polyline(routeLineCoordinates, { color: "green" });
        polyLineLayer.addTo(this.map);
      }

      routeTreesLayer = L.layerGroup(
        routeTrees.map((routeTree, index) => {
          const marker = L.marker(routeTree.coordinates, {
            icon: new L.AwesomeNumberMarkers({
              number: index + 1,
              markerColor: "blue",
            }),
          });

          const treeDescriptiveValues = descriptiveFieldValues[routeTree.id];
          if (treeDescriptiveValues) {
            const labels = {
              maxLabel: this.tooltipMaxLabelValue,
              avgLabel: this.tooltipAvgLabelValue,
            };
            const tooltipContent = Object.entries(treeDescriptiveValues)
              .filter(([, value]) => hasTooltipValue(value))
              .map(
                ([key, value]) =>
                  `<li>${escapeHtml(key)}: ${escapeHtml(formatTooltipValue(value, labels))}</li>`
              )
              .join("");
            if (tooltipContent) {
              marker.bindTooltip(`<ul>${tooltipContent}</ul>`);
            }
          }

          marker.on("click", () => {
            this.openRouteTree(routeTree);
          });
          return marker;
        })
      );
      routeTreesLayer.addTo(this.map);

      const comparisonLineCoordinates =
        comparisonRouteCoordinates.length > 0
          ? comparisonRouteCoordinates
          : comparisonRouteTrees.map((tree) => tree.coordinates);

      if (comparisonRouteLabel && comparisonLineCoordinates.length > 0) {
        comparisonPolyLineLayer = L.polyline(comparisonLineCoordinates, {
          color: "#64748b",
          dashArray: "8 8",
        }).addTo(this.map);
      }

      comparisonRouteTreesLayer = L.layerGroup(
        comparisonRouteTrees.map((routeTree) => {
          const marker = L.marker(routeTree.coordinates, {
            icon: new L.AwesomeNumberMarkers({
              number: "○",
              markerColor: "purple",
            }),
          });
          marker.on("click", () => {
            this.openRouteTree(routeTree);
          });
          return marker;
        })
      );

      if (comparisonRouteLabel) {
        comparisonRouteTreesLayer.addTo(this.map);
      }
    }

    if (usingOutOfBoundsRouteTreeFallback && polyLineLayer) {
      fitBounds(this.map, [polygonLayer, polyLineLayer], {
        padding: [40, 40],
      });
    } else {
      fitBounds(this.map, [polygonLayer]);
    }

    const currentIsEarlier = !earlierIsComparison;
    const earlierHeatmaps = currentIsEarlier
      ? heatmapFieldValues
      : comparisonHeatmapFieldValues;
    const laterHeatmaps = currentIsEarlier
      ? comparisonHeatmapFieldValues
      : heatmapFieldValues;
    const earlierMarkers = currentIsEarlier
      ? markerFieldValues
      : comparisonMarkerFieldValues;
    const laterMarkers = currentIsEarlier
      ? comparisonMarkerFieldValues
      : markerFieldValues;

    this.earlierLabel =
      this.element.dataset.earlierLabel ||
      (currentIsEarlier ? "Earlier" : comparisonRouteLabel);
    this.laterLabel =
      this.element.dataset.laterLabel ||
      (currentIsEarlier ? comparisonRouteLabel : "Later");

    let heatmapLayers = {};
    let comparisonHeatmapLayers = {};
    let markerLayers = {};
    let comparisonMarkerLayers = {};

    if (fieldFocus && mapViz === "heatmap") {
      this.earlierHeatPoints = flattenHeatPoints(earlierHeatmaps);
      this.laterHeatPoints = flattenHeatPoints(laterHeatmaps);
      this.sharedHeatMax = Math.max(
        maxHeatIntensity(this.earlierHeatPoints),
        maxHeatIntensity(this.laterHeatPoints),
        1
      );
      this.applyHeatmapMode(DEFAULT_HEATMAP_MODE);
      this.addHeatmapModeControl();
      this.addCoverageToggleControl();
      this.addRouteLegendControl({mode: "heatmap"});
    } else if (fieldFocus && mapViz === "presence") {
      markerLayers = this.createStandardMarkerLayers(
        earlierMarkers,
        this.earlierLabel,
        {fillOpacity: 0.85, weight: 2, radius: 7, color: EARLIER_COLOR}
      );
      comparisonMarkerLayers = this.createStandardMarkerLayers(
        laterMarkers,
        this.laterLabel,
        {fillOpacity: 0.85, weight: 2, radius: 7, color: LATER_COLOR}
      );
      this.addRouteLegendControl({mode: "presence"});
    } else {
      heatmapLayers = this.createStandardHeatmapLayers(heatmapFieldValues);
      comparisonHeatmapLayers = this.createStandardHeatmapLayers(
        comparisonHeatmapFieldValues,
        comparisonRouteLabel
      );
      markerLayers = this.createStandardMarkerLayers(markerFieldValues);
      comparisonMarkerLayers = this.createStandardMarkerLayers(
        comparisonMarkerFieldValues,
        comparisonRouteLabel,
        {fillOpacity: 0.35, weight: 2}
      );
    }

    this.fieldLayers = {...heatmapLayers, ...markerLayers};
    this.comparisonFieldLayers = {
      ...comparisonHeatmapLayers,
      ...comparisonMarkerLayers,
    };
    this.polyLineLayer = polyLineLayer;
    this.routeTreesLayer = routeTreesLayer;
    this.comparisonPolyLineLayer = comparisonPolyLineLayer;
    this.comparisonRouteTreesLayer = comparisonRouteTreesLayer;
    this.comparisonRouteLabel = comparisonRouteLabel;
    this.comparisonToggleTitle = comparisonToggleTitle;

    if (!fieldFocus) {
      const overlays = {};
      this.addFieldOverlays(overlays, heatmapLayers, markerLayers);
      this.addFieldOverlays(
        overlays,
        comparisonHeatmapLayers,
        comparisonMarkerLayers
      );
      if (polyLineLayer) overlays["🛤️ Recorrido"] = polyLineLayer;
      if (routeTreesLayer) overlays["🌳 Árboles"] = routeTreesLayer;
      if (comparisonPolyLineLayer) {
        overlays[`🛤️ Recorrido (${comparisonRouteLabel})`] =
          comparisonPolyLineLayer;
      }
      if (comparisonRouteLabel && comparisonRouteTreesLayer) {
        overlays[`🌳 Árboles (${comparisonRouteLabel})`] =
          comparisonRouteTreesLayer;
      }

      this.layerControl = L.control
        .layers(null, overlays, {
          collapsed: true,
          sortLayers: false,
        })
        .addTo(this.map);

      injectLayerControlStyle();
      this.addTogglePanel();
    }

    addReadOnlyGeomanControls(this.map);
  }

  disconnect() {
    Object.values(this.heatLayersByMode || {}).forEach((layers) => {
      (layers || []).forEach((layer) => {
        if (this.map?.hasLayer(layer)) this.map.removeLayer(layer);
      });
    });
    this.heatLayersByMode = {
      earlier: null,
      later: null,
      both: null,
      diff: null,
    };
    this.activeHeatmapMode = null;
    removeMap(this.map);
    this.map = null;
  }

  addFieldOverlays(overlays, heatmapLayers, markerLayers) {
    Object.entries(heatmapLayers).forEach(([name, layer]) => {
      overlays[`🔥 ${name}`] = layer;
    });
    Object.entries(markerLayers).forEach(([name, layer]) => {
      overlays[`📍 ${name}`] = layer;
    });
  }

  fieldLayerName(kind, fieldName, routeLabel = null) {
    const prefix =
      kind === "heatmap"
        ? this.element.dataset.heatmapLayerLabel || "Heatmap"
        : this.element.dataset.presenceLayerLabel || "Presence";
    return routeLabel
      ? `${prefix}: ${fieldName} (${routeLabel})`
      : `${prefix}: ${fieldName}`;
  }

  fieldPopupLabel(fieldName, routeLabel = null) {
    return routeLabel ? `${fieldName} (${routeLabel})` : fieldName;
  }

  createStandardHeatmapLayers(heatmapFieldValues, routeLabel = null) {
    const layers = {};
    for (const [customFieldName, values] of Object.entries(heatmapFieldValues)) {
      const heatmapPoints = values.filter(([, , value]) => value !== 0);
      const maxValue = maxHeatIntensity(values);
      if (maxValue === 0) continue;

      const layerName = this.fieldLayerName("heatmap", customFieldName, routeLabel);
      layers[layerName] = L.heatLayer(heatmapPoints, {
        radius: 15,
        blur: 10,
        minOpacity: routeLabel ? 0.2 : 0.4,
        max: maxValue,
        gradient: {
          0.2: "blue",
          0.4: "cyan",
          0.6: "lime",
          0.8: "yellow",
          1.0: "red",
        },
      });
      addLayerWhenMapVisible(this.map, layers[layerName]);
    }
    return layers;
  }

  createStandardMarkerLayers(
    markerFieldValues,
    routeLabel = null,
    style = {}
  ) {
    const fillOpacity = style.fillOpacity ?? 1;
    const weight = style.weight ?? 1;
    const fixedColor = style.color;
    const radius = style.radius;
    const layers = {};

    for (const [customFieldName, values] of Object.entries(markerFieldValues)) {
      const markers = values.map(({coordinates, color}) => {
        const markerColor = fixedColor ?? color;
        const options = {
          color: markerColor,
          fillColor: markerColor,
          fillOpacity,
          weight,
        };
        if (radius != null) options.radius = radius;

        return L.circleMarker(coordinates, options).bindPopup(
          this.fieldPopupLabel(customFieldName, routeLabel)
        );
      });

      const layerName = this.fieldLayerName(
        "presence",
        customFieldName,
        routeLabel
      );
      layers[layerName] = L.layerGroup(markers).addTo(this.map);
    }
    return layers;
  }

  clearCoverageLayer() {
    if (this.coverageLayer && this.map.hasLayer(this.coverageLayer)) {
      this.map.removeLayer(this.coverageLayer);
    }
    this.coverageLayer = null;
  }

  hideHeatLayersForMode(mode) {
    if (!mode || !this.heatLayersByMode[mode]) return;

    this.heatLayersByMode[mode].forEach((layer) => {
      if (this.map.hasLayer(layer)) this.map.removeLayer(layer);
    });
  }

  showHeatLayers(layers) {
    layers.forEach((layer) => addLayerWhenMapVisible(this.map, layer));
  }

  createHeatLayer(points, gradient, max, minOpacity = ABSOLUTE_HEAT_MIN_OPACITY) {
    if (!points || points.length === 0) return null;

    return L.heatLayer(points, {
      radius: HEAT_RADIUS,
      blur: HEAT_BLUR,
      minOpacity,
      max,
      gradient,
    });
  }

  ensureHeatLayersForMode(mode) {
    if (this.heatLayersByMode[mode]) return this.heatLayersByMode[mode];

    const absoluteModeLayers = {
      earlier: [
        [this.earlierHeatPoints, EARLIER_GRADIENT, ABSOLUTE_HEAT_MIN_OPACITY],
      ],
      later: [
        [this.laterHeatPoints, LATER_GRADIENT, ABSOLUTE_HEAT_MIN_OPACITY],
      ],
      both: [
        [this.earlierHeatPoints, EARLIER_GRADIENT, BOTH_HEAT_MIN_OPACITY],
        [this.laterHeatPoints, LATER_GRADIENT, BOTH_HEAT_MIN_OPACITY],
      ],
    };

    let layers = [];
    if (absoluteModeLayers[mode]) {
      layers = absoluteModeLayers[mode]
        .map(([points, gradient, minOpacity]) =>
          this.createHeatLayer(points, gradient, this.sharedHeatMax, minOpacity)
        )
        .filter(Boolean);
    } else if (mode === "diff") {
      this.diffResult ||= buildDiffHeatPoints(
        this.earlierHeatPoints,
        this.laterHeatPoints
      );
      const sharedMax = Math.max(this.diffResult.maxAbs, 1);
      layers = [
        this.createHeatLayer(
          this.diffResult.decreases,
          DIFF_BETTER_GRADIENT,
          sharedMax,
          DIFF_HEAT_MIN_OPACITY
        ),
        this.createHeatLayer(
          this.diffResult.increases,
          DIFF_WORSE_GRADIENT,
          sharedMax,
          DIFF_HEAT_MIN_OPACITY
        ),
      ].filter(Boolean);
    }

    this.heatLayersByMode[mode] = layers;
    return layers;
  }

  applyHeatmapMode(mode) {
    this.hideHeatLayersForMode(this.activeHeatmapMode);

    if (mode !== "diff") {
      this.coverageVisible = false;
      this.clearCoverageLayer();
    }

    this.heatmapMode = mode;
    this.showHeatLayers(this.ensureHeatLayersForMode(mode));
    this.activeHeatmapMode = mode;

    if (mode === "diff" && this.coverageVisible) {
      if (this.coverageLayer) {
        if (!this.map.hasLayer(this.coverageLayer)) {
          this.coverageLayer.addTo(this.map);
        }
      } else {
        this.renderCoverageOverlay();
      }
    }

    this.updateHeatmapModeButtons();
    this.updateCoverageToggleVisibility();
    this.updateRouteLegendForMode(mode);
  }

  addCoverageToggleControl() {
    const CoverageControl = L.Control.extend({
      options: {position: "topleft"},
      onAdd: () => {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar bg-base-100/95 rounded-lg border border-base-300 shadow-sm mt-1 hidden"
        );
        const button = L.DomUtil.create(
          "button",
          "px-2 py-1.5 text-xs font-medium w-full text-left",
          container
        );
        button.type = "button";
        button.textContent =
          this.element.dataset.coverageToggleLabel || "Coverage";
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleCoverageOverlay();
        });

        this.coverageToggleContainer = container;
        this.coverageToggleButton = button;
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);
        this.updateCoverageToggleVisibility();
        return container;
      },
    });

    this.map.addControl(new CoverageControl());
  }

  toggleCoverageOverlay() {
    if (this.heatmapMode !== "diff") return;

    this.coverageVisible = !this.coverageVisible;
    if (this.coverageVisible) {
      if (this.coverageLayer) {
        this.coverageLayer.addTo(this.map);
      } else {
        this.renderCoverageOverlay();
      }
    } else if (this.coverageLayer && this.map.hasLayer(this.coverageLayer)) {
      this.map.removeLayer(this.coverageLayer);
    }
    this.updateCoverageToggleButton();
    this.updateRouteLegendForMode("diff");
  }

  renderCoverageOverlay() {
    this.clearCoverageLayer();
    if (!this.diffResult) return;

    const markers = [
      ...this.diffResult.earlierOnly.map(([lat, lon]) =>
        L.circleMarker([lat, lon], {
          color: COVERAGE_EARLIER_COLOR,
          fillColor: "#ffffff",
          fillOpacity: 0.15,
          radius: 6,
          weight: 2,
        })
      ),
      ...this.diffResult.laterOnly.map(([lat, lon]) =>
        L.circleMarker([lat, lon], {
          color: COVERAGE_LATER_COLOR,
          fillColor: "#ffffff",
          fillOpacity: 0.15,
          radius: 6,
          weight: 2,
        })
      ),
    ];

    if (markers.length === 0) return;

    this.coverageLayer = L.layerGroup(markers).addTo(this.map);
  }

  updateCoverageToggleVisibility() {
    if (!this.coverageToggleContainer) return;

    const show = this.heatmapMode === "diff";
    this.coverageToggleContainer.classList.toggle("hidden", !show);
    if (show) this.updateCoverageToggleButton();
  }

  updateCoverageToggleButton() {
    if (!this.coverageToggleButton) return;

    this.coverageToggleButton.classList.toggle("bg-primary", this.coverageVisible);
    this.coverageToggleButton.classList.toggle(
      "text-primary-content",
      this.coverageVisible
    );
    this.coverageToggleButton.classList.toggle("bg-base-100", !this.coverageVisible);
    this.coverageToggleButton.classList.toggle(
      "text-base-content",
      !this.coverageVisible
    );
  }

  addHeatmapModeControl() {
    const hasEarlier = this.earlierHeatPoints.length > 0;
    const hasLater = this.laterHeatPoints.length > 0;
    const hasDiff = hasEarlier && hasLater;

    const modes = [
      {
        id: "earlier",
        label: this.element.dataset.heatmapModeEarlierLabel || "Earlier",
        enabled: hasEarlier,
      },
      {
        id: "later",
        label: this.element.dataset.heatmapModeLaterLabel || "Later",
        enabled: hasLater,
      },
      {
        id: "both",
        label: this.element.dataset.heatmapModeBothLabel || "Both",
        enabled: hasEarlier || hasLater,
      },
      {
        id: "diff",
        label: this.element.dataset.heatmapModeDiffLabel || "Diff",
        enabled: hasDiff,
      },
    ];

    const ModeControl = L.Control.extend({
      options: {position: "topleft"},
      onAdd: () => {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar bg-base-100/95 rounded-lg border border-base-300 shadow-sm overflow-hidden flex"
        );
        this.heatmapModeButtons = {};

        modes.forEach((mode) => {
          const button = L.DomUtil.create(
            "button",
            "px-2 py-1.5 text-xs font-medium border-r border-base-300 last:border-r-0",
            container
          );
          button.type = "button";
          button.textContent = mode.label;
          button.disabled = !mode.enabled;
          if (!mode.enabled) {
            button.classList.add("opacity-40", "cursor-not-allowed");
          }
          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!mode.enabled) return;
            this.applyHeatmapMode(mode.id);
          });
          this.heatmapModeButtons[mode.id] = button;
        });

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);
        this.updateHeatmapModeButtons();
        return container;
      },
    });

    this.map.addControl(new ModeControl());
  }

  updateHeatmapModeButtons() {
    if (!this.heatmapModeButtons) return;

    Object.entries(this.heatmapModeButtons).forEach(([id, button]) => {
      const active = id === this.heatmapMode;
      button.classList.toggle("bg-primary", active);
      button.classList.toggle("text-primary-content", active);
      button.classList.toggle("bg-base-100", !active);
      button.classList.toggle("text-base-content", !active);
    });
  }

  addRouteLegendControl({mode}) {
    const LegendControl = L.Control.extend({
      options: {position: "bottomleft"},
      onAdd: () => {
        const container = L.DomUtil.create(
          "div",
          "p-2 space-y-1.5 text-xs rounded-lg border shadow-sm bg-base-100/95 border-base-300"
        );
        this.legendContainer = container;
        this.renderLegendContent(mode);
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);
        return container;
      },
    });

    this.map.addControl(new LegendControl());
  }

  updateRouteLegendForMode(mode) {
    if (!this.legendContainer) return;
    this.renderLegendContent(mode === "diff" ? "diff" : "heatmap");
  }

  renderLegendContent(mode) {
    if (!this.legendContainer) return;
    this.legendContainer.innerHTML = "";

    if (mode === "diff") {
      const hasMatchedChange =
        (this.diffResult?.increases?.length || 0) > 0 ||
        (this.diffResult?.decreases?.length || 0) > 0;

      if (!hasMatchedChange) {
        const empty = L.DomUtil.create(
          "div",
          "text-base-content/70 max-w-48",
          this.legendContainer
        );
        empty.textContent =
          this.element.dataset.legendDiffEmptyLabel ||
          "No overlapping samples to compare";
      } else {
        this.appendLegendRow(
          this.legendContainer,
          DIFF_WORSE_COLOR,
          this.element.dataset.legendWorseLabel || "Worse"
        );
        this.appendLegendRow(
          this.legendContainer,
          DIFF_BETTER_COLOR,
          this.element.dataset.legendBetterLabel || "Better"
        );
      }

      if (this.coverageVisible) {
        this.appendLegendRow(
          this.legendContainer,
          COVERAGE_EARLIER_COLOR,
          this.element.dataset.legendOnlyEarlierLabel || "Only earlier",
          {hollow: true}
        );
        this.appendLegendRow(
          this.legendContainer,
          COVERAGE_LATER_COLOR,
          this.element.dataset.legendOnlyLaterLabel || "Only later",
          {hollow: true}
        );
      }
      return;
    }

    this.appendLegendRow(this.legendContainer, EARLIER_COLOR, this.earlierLabel);
    this.appendLegendRow(this.legendContainer, LATER_COLOR, this.laterLabel);
  }

  appendLegendRow(container, color, label, {hollow = false} = {}) {
    const row = L.DomUtil.create("div", "flex gap-2 items-center", container);
    const swatch = L.DomUtil.create(
      "span",
      hollow
        ? "w-3 h-3 rounded-full shrink-0 bg-base-100"
        : "w-3 h-3 rounded-full shrink-0",
      row
    );
    if (hollow) {
      swatch.style.border = `2px solid ${color}`;
    } else {
      swatch.style.backgroundColor = color;
    }
    const text = L.DomUtil.create("span", "", row);
    text.textContent = label;
  }

  addTogglePanel() {
    const hasFields = Object.keys(this.fieldLayers).length > 0;
    const hasLine = !!this.polyLineLayer;
    const hasTrees = !!this.routeTreesLayer;
    const comparisonLayers = [
      this.comparisonPolyLineLayer,
      this.comparisonRouteTreesLayer,
      ...Object.values(this.comparisonFieldLayers),
    ].filter(Boolean);
    const hasComparison = this.comparisonRouteLabel && comparisonLayers.length > 0;

    if (!hasFields && !hasLine && !hasTrees) return;

    const TogglePanel = L.Control.extend({
      options: {position: "bottomleft"},

      onAdd: () => {
        const container = createTogglePanelContainer();
        let btnIndex = 0;

        if (hasLine) {
          createToggleButton(container, {
            icon: MAP_TOGGLE_ICONS.route,
            label: "Recorrido",
            title: "Mostrar/ocultar recorrido",
            activeColor: "#10b981",
            visible: true,
            isFirst: btnIndex++ === 0,
            onToggle: (update) => {
              const isVisible = this.map.hasLayer(this.polyLineLayer);
              if (isVisible) {
                this.map.removeLayer(this.polyLineLayer);
              } else {
                this.map.addLayer(this.polyLineLayer);
              }
              update(!isVisible);
            },
          });
        }

        if (hasTrees) {
          createToggleButton(container, {
            icon: MAP_TOGGLE_ICONS.trees,
            label: "Árboles",
            title: "Mostrar/ocultar árboles",
            activeColor: "#10b981",
            visible: true,
            isFirst: btnIndex++ === 0,
            onToggle: (update) => {
              const isVisible = this.map.hasLayer(this.routeTreesLayer);
              if (isVisible) {
                this.map.removeLayer(this.routeTreesLayer);
              } else {
                this.map.addLayer(this.routeTreesLayer);
              }
              update(!isVisible);
            },
          });
        }

        if (hasFields) {
          createToggleButton(container, {
            icon: MAP_TOGGLE_ICONS.fields,
            label: "Campos",
            title: "Mostrar/ocultar campos de datos",
            activeColor: "#3b82f6",
            visible: true,
            isFirst: btnIndex++ === 0,
            onToggle: (update) => {
              const anyVisible = Object.values(this.fieldLayers).some(
                (layer) => this.map.hasLayer(layer)
              );
              Object.values(this.fieldLayers).forEach((layer) => {
                if (anyVisible) {
                  this.map.removeLayer(layer);
                } else {
                  this.map.addLayer(layer);
                }
              });
              update(!anyVisible);
            },
          });
        }

        if (hasComparison) {
          createToggleButton(container, {
            icon: MAP_TOGGLE_ICONS.route,
            label: this.comparisonRouteLabel,
            title: this.comparisonToggleTitle,
            activeColor: "#7c3aed",
            visible: true,
            isFirst: btnIndex++ === 0,
            onToggle: (update) => {
              const anyVisible = comparisonLayers.some((layer) =>
                this.map.hasLayer(layer)
              );
              comparisonLayers.forEach((layer) => {
                if (anyVisible) {
                  this.map.removeLayer(layer);
                } else {
                  this.map.addLayer(layer);
                }
              });
              update(!anyVisible);
            },
          });
        }

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    this.map.addControl(new TogglePanel());
  }

  openRouteTree(routeTree) {
    openRouteTreeInNewTab(routeTree);
  }
}

function flattenHeatPoints(heatmapFieldValues) {
  return Object.values(heatmapFieldValues)
    .flat()
    .filter(([, , value]) => value !== 0 && value != null);
}

function maxHeatIntensity(points) {
  let max = 0;
  for (const [, , value] of points) {
    if (value > max) max = value;
  }
  return max;
}

export function buildDiffHeatPoints(earlierPoints, laterPoints) {
  const earlier = earlierPoints.map(([lat, lon, value]) => ({
    lat,
    lon,
    value,
    used: false,
  }));
  const grid = buildPointGrid(earlier, DIFF_MATCH_RADIUS_DEGREES);
  const increases = [];
  const decreases = [];
  const earlierOnly = [];
  const laterOnly = [];
  let maxAbs = 0;

  laterPoints.forEach(([lat, lon, value]) => {
    const match = findNearestUnusedInGrid(
      grid,
      lat,
      lon,
      DIFF_MATCH_RADIUS_DEGREES
    );
    if (match) {
      match.used = true;
      const delta = value - match.value;
      if (delta === 0) return;

      const abs = Math.abs(delta);
      maxAbs = Math.max(maxAbs, abs);
      if (delta > 0) {
        increases.push([lat, lon, abs]);
      } else {
        decreases.push([lat, lon, abs]);
      }
      return;
    }

    if (value > 0) {
      laterOnly.push([lat, lon, value]);
    }
  });

  earlier.forEach((point) => {
    if (point.used || !(point.value > 0)) return;
    earlierOnly.push([point.lat, point.lon, point.value]);
  });

  return {increases, decreases, earlierOnly, laterOnly, maxAbs};
}

function cellKey(lat, lon, cellSize) {
  return `${Math.floor(lat / cellSize)},${Math.floor(lon / cellSize)}`;
}

function buildPointGrid(points, cellSize) {
  const grid = new Map();
  points.forEach((point) => {
    const key = cellKey(point.lat, point.lon, cellSize);
    const bucket = grid.get(key);
    if (bucket) {
      bucket.push(point);
    } else {
      grid.set(key, [point]);
    }
  });
  return grid;
}

function findNearestUnusedInGrid(grid, lat, lon, radiusDegrees) {
  const centerLat = Math.floor(lat / radiusDegrees);
  const centerLon = Math.floor(lon / radiusDegrees);
  let best = null;
  let bestDistance = Infinity;

  for (let dLat = -1; dLat <= 1; dLat++) {
    for (let dLon = -1; dLon <= 1; dLon++) {
      const bucket = grid.get(`${centerLat + dLat},${centerLon + dLon}`);
      if (!bucket) continue;

      for (const candidate of bucket) {
        if (candidate.used) continue;

        const distance = Math.hypot(candidate.lat - lat, candidate.lon - lon);
        if (distance <= radiusDegrees && distance < bestDistance) {
          best = candidate;
          bestDistance = distance;
        }
      }
    }
  }

  return best;
}
